import { BodyCell, DataGrid } from '@tableau/widgets-datagrid';
import * as React from 'react';
import * as _ from 'underscore';

import { CommonStyles } from '../../common/react/CommonStyles';
import { AngularToReactDataGridBridge } from '../../common/react/components/dataGrid/AngularToReactDataGridBridge';
import { SortableHeader } from '../../common/react/components/dataGrid/SortableHeader';
import { StyledCellFormatter } from '../../common/react/components/dataGrid/StyledCellWrapper';
import { FormattedMatch } from '../../common/react/FormattedMatch';
import { Filter } from '../helpers/Filter';
import { Keys } from '../helpers/Keys';
import { Module } from '../Module';
import { SiteNameSansIds } from '../resources/SiteNameSansIds';
import { ISiteNameSansId } from '../ServerApi/types/ISiteNameSansId';
import { SiteSwitchService } from '../services/SiteSwitchService';

export interface ILoginSitePickerScope extends ng.IScope {
  site: string;
  siteSearchValue: string;
  handleKeyEvent: ($event: JQueryEventObject) => void;
  select: (siteName: ISiteNameSansId) => void;
  currentRow: {
    index: number;
    object?: ISiteNameSansId;
  };

  dataGridProps: DataGrid.Props & React.ClassAttributes<DataGrid>;
}

export class LoginSitePickerCtrl {


  private static DataGridCellStyle: React.CSSProperties = {
    fontSize: CommonStyles.Sizes.LargeFontSize,
    // This is to make sure the width of the cell is not wider than the grid for
    // mobile scenarios. TFSID: 630270
    maxWidth: '100%',
  };

  private dataGridInstance: DataGrid;

  public static $inject = [
    '$scope',
    'SiteNamesAcrossAllPods',
    'SiteSwitchService',
    'AngularToReactDataGridBridge'
  ];

  constructor(
    private $scope: ILoginSitePickerScope,
            siteNames: SiteNameSansIds,
            siteSwitchService: SiteSwitchService,
    private AngularToReactDataGridBridge: AngularToReactDataGridBridge
  ) {

    this.configureLoginSitePicker();

    $scope.siteSearchValue = '';

    $scope.$watch('siteSearchValue', (newValue: string) => {
      let query = siteNames.all();
      if (_.isString(newValue) && newValue.length > 0) {
        query = query.withFilters([Filter.search(newValue)]);
      }
      $scope.currentRow.index = 0;

      this.AngularToReactDataGridBridge.updateReactDataGridRowStore({scope: this.$scope, sliceable: query});
    });

    $scope.currentRow = {
      index: 0
    };

    $scope.select = (siteName: ISiteNameSansId) => {
      if (siteName) {
        const siteUrl: string = siteName.urlName;
        siteSwitchService.switchIfNeededAndRedirectToStartPage(siteUrl);
      }
    };

    $scope.$watch('site', (newSiteName: ISiteNameSansId) => {
      if (!newSiteName) return;
      $scope.select(newSiteName);
    });

    $scope.handleKeyEvent = ($event: JQueryEventObject) => {
      switch ($event.which) {

        case Keys.ArrowDown:
          $event.preventDefault();
          $scope.currentRow.index++;
          break;

        case Keys.ArrowUp:
          $event.preventDefault();
          $scope.currentRow.index--;
          break;

        case Keys.Enter:
          $scope.select($scope.currentRow.object);
          break;

        case Keys.Escape:
          $scope.siteSearchValue = '';
          break;
      }
    };
  }

  private configureLoginSitePicker() {
    const cols: DataGrid.Column[] = [
      {
        name: 'siteName-col',
        dataKey: 'name',
        headerText: '',
        growToFit: true,
        headerFormatter: SortableHeader.Formatter(),
        cellFormatter: StyledCellFormatter(this.siteNameFormatter(this.$scope), LoginSitePickerCtrl.DataGridCellStyle),
      }
    ];

    this.AngularToReactDataGridBridge.attachReactDataGridToScope({
      $scope: this.$scope,
      columns: cols,
      emptyChild: this.AngularToReactDataGridBridge.emptyTemplateElementFor({}),
      canShowFilterPanel: false
    });

    const cellClickHandler = (item: any) => this.$scope.select(item);

    const rowFocusedHandler = (row: DataGrid.Row, rowIndex: number) => {
      this.$scope.currentRow = {
        index: rowIndex,
        object: row.data as ISiteNameSansId
      };
    };

    // Keep React grid focused row in sync with $scope.currentRow
    this.$scope.$watch(() => this.$scope.currentRow.index, () => {

      this.$scope.dataGridProps.requestedFocusedRowIndex = this.$scope.currentRow.index;

      // TODO: TFS 769643 - The following code might help us properly implement keyboard navigation with Data Grid 2
      // this.dataGridInstance.setState(null, () => this.dataGridInstance.focusCell(this.$scope.currentRow.index));
    });

    _.extend(this.$scope.dataGridProps, {
      showHeaders: false,
      showHorizontalGridLines: false,
      focusedIndex: 0,
      onCellClick: cellClickHandler,
      onRowClick: cellClickHandler,
      onRowFocused: rowFocusedHandler,
      ref: (instance: DataGrid) => this.dataGridInstance = instance,
      updated: (rows: DataGrid.Row[]) => {
        const row = rows[this.$scope.currentRow.index];
        this.$scope.currentRow.object = row ? row.data as ISiteNameSansId : null;
      }
    });
  }

  private siteNameFormatter(scope: ILoginSitePickerScope): React.StatelessComponent<BodyCell.Props> {
    return function SiteNameCell(props: BodyCell.Props) {
      return React.createElement(FormattedMatch, {
        text: props.value,
        searchTerm: scope.siteSearchValue,
        matchPrefixesOnly: false
      });
    };
  }

}

Module.instance().controller('LoginSitePickerCtrl', LoginSitePickerCtrl);
